import { useState, useEffect, useCallback } from 'react';
import { perplexityService } from '@services/api/perplexity.service';
import { StockEvent } from '@/types/perplexity.types';
import { supabase } from '@services/supabase/supabaseClient';
import { isTopTicker } from '@/constants/topTickers';

interface UseStockEventsReturn {
  events: StockEvent[];
  isLoading: boolean;
  error: string | null;
  refreshEvents: () => Promise<void>;
  lastUpdated: Date | null;
}

// Cache settings - per stock
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours (matches cron job frequency)
const LOADING_MESSAGE = 'Getting fresh vibes for you...';

// In-memory session cache (separate from database cache)
const sessionCache: Map<string, { data: StockEvent[]; timestamp: number }> = new Map();

export const useStockEvents = (ticker: string, companyName: string): UseStockEventsReturn => {
  const [events, setEvents] = useState<StockEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const fetchEvents = useCallback(
    async (bypassCache: boolean = false) => {
      try {
        setIsLoading(true);
        setError(null);

        // Step 1: Check session cache first (fastest)
        if (!bypassCache && sessionCache.has(ticker)) {
          const cached = sessionCache.get(ticker)!;
          const now = Date.now();
          if (now - cached.timestamp < CACHE_DURATION) {
            console.log(`📰 Using session cached events for ${ticker}`);
            setEvents(cached.data);
            setLastUpdated(new Date(cached.timestamp));
            setIsLoading(false);
            return;
          }
        }

        // Step 2: If this is a top ticker, try database cache (pre-fetched by cron)
        if (!bypassCache && isTopTicker(ticker)) {
          console.log(`📰 Checking database cache for ${ticker}...`);
          const { data: dbCache, error: dbError } = await supabase
            .from('stock_events_cache')
            .select('events, fetched_at')
            .eq('ticker', ticker.toUpperCase())
            .single();

          if (!dbError && dbCache) {
            const fetchedAt = new Date(dbCache.fetched_at).getTime();
            const now = Date.now();
            const isFresh = now - fetchedAt < CACHE_DURATION;

            if (isFresh) {
              console.log(
                `✅ Using fresh database cache for ${ticker} (age:`,
                Math.round((now - fetchedAt) / (60 * 60 * 1000)),
                'hours)'
              );
              const stockEvents = dbCache.events as StockEvent[];

              // Update session cache
              sessionCache.set(ticker, {
                data: stockEvents,
                timestamp: fetchedAt,
              });

              setEvents(stockEvents);
              setLastUpdated(new Date(fetchedAt));
              setIsLoading(false);
              return;
            } else {
              console.log(
                `⚠️  Database cache for ${ticker} is stale (age:`,
                Math.round((now - fetchedAt) / (60 * 60 * 1000)),
                'hours)'
              );
            }
          }
        }

        // Step 3: Fallback to direct Perplexity API call
        console.log(`📰 ${LOADING_MESSAGE} (${ticker})`);
        const result = await perplexityService.getStockEventsStructured(
          ticker,
          companyName,
          'week'
        );

        if (result.error) {
          throw new Error(result.error.message);
        }

        if (result.data) {
          const now = Date.now();

          // Update session cache only (not database - cron handles that for top tickers)
          sessionCache.set(ticker, {
            data: result.data,
            timestamp: now,
          });

          setEvents(result.data);
          setLastUpdated(new Date(now));
          console.log(
            `✅ Stock events for ${ticker} fetched from API:`,
            result.data.length,
            'events'
          );
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch stock events';
        setError(errorMessage);
        console.error(`useStockEvents error for ${ticker}:`, errorMessage);
      } finally {
        setIsLoading(false);
      }
    },
    [ticker, companyName]
  );

  // Initial fetch on mount or when ticker changes
  useEffect(() => {
    fetchEvents(false); // Use cache on initial load
  }, [fetchEvents]);

  // Refresh function (bypasses all caches and forces API call)
  const refreshEvents = useCallback(async () => {
    await fetchEvents(true); // bypassCache = true
  }, [fetchEvents]);

  return {
    events,
    isLoading,
    error,
    refreshEvents,
    lastUpdated,
  };
};
